import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Box, Typography, useTheme } from '@mui/material';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import clsx from 'clsx';
import { PaymentProviderType } from 'components/user/model/User';
import { userApi } from 'components/user/userApi';
import { isDefined } from 'lib/typeInference';
import { useAuth } from 'lib/useAuth';
import { IAddCardComponentClasses } from '../PaymentProvider';
import { useSnackbar } from 'notistack';
import { ButtonV2 } from 'ui/ButtonV2';

const PREFIX = 'StripeAddCardComponent';

const classes = {
    buttonContainer: `${PREFIX}buttonContainer`,
    stripeBase: `${PREFIX}stripeBase`
};

const StyledBox = styled(Box)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    [`& .${classes.buttonContainer}`]: {
        marginTop: 'auto'
    },

    [`& .${classes.stripeBase}`]: {
        marginTop: theme.spacing(2)
    }
}));

interface IProps {
    postCardAddition: () => void;
    classes?: IAddCardComponentClasses;
}

export const StripeAddCardComponent: React.FC<React.PropsWithChildren<IProps>> = ({
    postCardAddition,
    classes: overrideClasses = {}
}) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const [addCardInProgress, setAddCardInProgress] = React.useState(false);
    const [errorMessage, setError] = React.useState<string>();
    const { isLoggedIn, hasSession } = useAuth();
    const stripe = useStripe();
    const elements = useElements();
    const { enqueueSnackbar } = useSnackbar();

    const handleAddCardSubmit = React.useCallback(async () => {
        setAddCardInProgress(true);
        setError(undefined);
        try {
            if (!hasSession) {
                throw new Error('User is not authenticated');
            }

            if (!stripe || !elements) {
                // Stripe.js has not loaded yet. Make sure to disable
                // form submission until Stripe.js has loaded.
                return;
            }

            const cardElement = elements.getElement(CardElement);

            if (!!cardElement) {
                const { error, paymentMethod } = await stripe.createPaymentMethod({
                    type: 'card',
                    card: cardElement
                });

                if (!!error) {
                    setError(t('CHECKOUT_ERROR_VALIDATING_CARD'));
                } else {
                    try {
                        const { stripe: stripeSetupData } = await userApi.getCardSetupData();
                        const intentResponse = await stripe.confirmCardSetup(
                            stripeSetupData.setupIntentClientSecret,
                            {
                                payment_method: { card: cardElement }
                            }
                        );

                        if (!!paymentMethod?.card && !intentResponse.error) {
                            const {
                                brand,
                                last4,
                                exp_month: expMonth,
                                exp_year: expYear
                            } = paymentMethod.card;
                            try {
                                await userApi.createCard({
                                    last4,
                                    expires: `${expMonth > 9 ? expMonth : `0${expMonth}`}${expYear % 100}`,
                                    gatewayId: stripeSetupData.setupIntentCustomerId,
                                    cardType: PaymentProviderType.STRIPE,
                                    type: brand,
                                    cardId: intentResponse.setupIntent.payment_method ?? paymentMethod.id
                                });
                                postCardAddition();
                                enqueueSnackbar(t('CHECKOUT_CARD_ADDED'), { variant: 'success' });
                            } catch (err: any) {
                                setError(err.data?.message || t('GENERAL_GENERIC_ERROR'));
                            }
                        } else {
                            throw new Error('Card not found');
                        }
                    } catch {
                        setError(t('CHECKOUT_ERROR_VALIDATING_CARD'));
                    }
                }
            } else {
                return;
            }
        } catch {
            setError(t('CHECKOUT_ERROR_VALIDATING_CARD'));
        } finally {
            setAddCardInProgress(false);
        }
    }, [elements, enqueueSnackbar, hasSession, postCardAddition, stripe, t]);
    const handleCardChange = React.useCallback(() => {
        setError(undefined);
    }, []);
    return (
        <StyledBox>
            <CardElement
                onChange={handleCardChange}
                options={{
                    style: {
                        base: {
                            fontSize: '16px',
                            color: theme.palette.text.primary
                        }
                    },
                    classes: {
                        base: clsx(classes.stripeBase, overrideClasses.root)
                    }
                }}
            />
            <Box pt={2}>
                {isDefined(errorMessage) && <Typography color="error">{errorMessage}</Typography>}
            </Box>
            <div className={clsx(classes.buttonContainer, overrideClasses.buttonContainer)}>
                <ButtonV2
                    disabled={addCardInProgress || !stripe}
                    loading={addCardInProgress}
                    onClick={handleAddCardSubmit}
                    className={overrideClasses.button}
                    color="primary"
                    variant="contained"
                    fullWidth
                >
                    {isLoggedIn ? t('CHECKOUT_ADD_CARD') : t('CHECKOUT_USE_CARD')}
                </ButtonV2>
            </div>
        </StyledBox>
    );
};
